<script setup lang="ts">

import {usePageContext} from "vike-vue/usePageContext";
import {onMounted, onUnmounted, ref} from "vue";
import Swiper from "swiper";
import {Navigation, Grid, Scrollbar, Autoplay} from "swiper/modules";
import "swiper/css";
import 'swiper/css/navigation';
import 'swiper/css/grid';
import 'swiper/css/scrollbar';
import {bannerPath} from "../../../core/utils/bannerSlug";
import SpinWheelPopup from "../../../core/cpn/SpinWheelPopup/SpinWheelPopup.vue";


const pageContext = usePageContext();


const {banners} = pageContext.data;

const HERO_SLOT_COUNT = 4
const heroImages = ref<any[]>([])
const heroPlaceholderSlots = Array.from({length: HERO_SLOT_COUNT}, (_, i) => i)
let heroInterval: ReturnType<typeof setInterval> | null = null
let heroUpdateToken = 0
let heroMounted = true

function getDesignImageUrl(design: any) {
  return design?.img_url || design?.jpg_url || ""
}

function buildHeroSet(allDesigns: any[]) {
  const shuffled = [...allDesigns].sort(() => 0.5 - Math.random())
  const selected = shuffled.slice(0, HERO_SLOT_COUNT)
  if (!selected.length) return []

  const baseItems = [...selected]
  while (selected.length < HERO_SLOT_COUNT) {
    selected.push(baseItems[selected.length % baseItems.length])
  }

  return selected.map((design, index) => ({
    ...design,
    __heroSlotKey: `${design?._id || design?.label || "hero"}-${index}`
  }))
}

async function preloadAndDecodeImage(url: string) {
  if (!url) return

  await new Promise<void>((resolve) => {
    const image = new Image()
    let done = false
    const finish = () => {
      if (done) return
      done = true
      resolve()
    }

    const decodeAndFinish = () => {
      if (typeof image.decode === "function") {
        image.decode().catch(() => {}).finally(finish)
        return
      }
      finish()
    }

    image.onload = decodeAndFinish
    image.onerror = finish
    image.src = url

    if (image.complete) decodeAndFinish()
  })
}

async function pickRandomHeroImages() {
  if (!banners) return

  const allDesigns = banners.flatMap((c: any) => c.designs || [])
  if (!allDesigns.length) return

  const nextImages = buildHeroSet(allDesigns)
  const currentToken = ++heroUpdateToken

  await Promise.all(
    nextImages.map((design: any) => preloadAndDecodeImage(getDesignImageUrl(design)))
  )

  if (!heroMounted || currentToken !== heroUpdateToken) return
  heroImages.value = nextImages
}

onMounted(async () => {
  await pickRandomHeroImages()

  heroInterval = setInterval(() => {
    pickRandomHeroImages()
  }, 4000)
})

onUnmounted(() => {
  heroMounted = false
  if (heroInterval) clearInterval(heroInterval)
})

onMounted(() => {
  // init every swiper with per-section tweaks
  document.querySelectorAll<HTMLElement>(".swiper").forEach((el) => {
    const isSoccer = el.classList.contains("Baseball");

    const nextBtn = el.querySelector('.swiper-button-next') as HTMLElement | null;
    const prevBtn = el.querySelector('.swiper-button-prev') as HTMLElement | null;

    const swiper = new Swiper(el, {
      modules: [Navigation, Grid, Scrollbar, Autoplay],
      navigation: {
        nextEl: nextBtn as any,
        prevEl: prevBtn as any,
      },
      slidesPerView: 'auto',
      spaceBetween: 10,
      loop: isSoccer,
      freeMode: true,
      speed: isSoccer ? 12000 : 400,
      allowTouchMove: true,
      centeredSlides: false,
      grid: { rows: 3, fill: 'row' },
      scrollbar: {
        el: el.querySelector('.swiper-scrollbar'),
        draggable: true,
      },
      autoplay: isSoccer
        ? { delay: 0, disableOnInteraction: true, pauseOnMouseEnter: false }
        : false,
    });

    // For Soccer: when user clicks Prev/Next or drags, stop autoplay so it behaves like a normal swiper
    if (isSoccer) {
      const stopAuto = () => {
        // Stop autoplay unconditionally and prevent it from restarting
        // @ts-ignore
        if (swiper.autoplay && typeof swiper.autoplay.stop === 'function') {
          // @ts-ignore
          swiper.autoplay.stop();
        }
        // Ensure autoplay is fully disabled going forward
        // @ts-ignore
        swiper.params.autoplay = false as any;
        // Make manual navigation feel normal
        swiper.params.speed = 400;
        swiper.params.allowTouchMove = true;
        swiper.params.allowSlideNext = true;
        swiper.params.allowSlidePrev = true;
        swiper.update();
      };

      // Stop on every user navigation click (and let Navigation proceed normally)
      if (nextBtn) nextBtn.addEventListener('click', stopAuto);
      if (prevBtn) prevBtn.addEventListener('click', stopAuto);

      // Also stop when the user starts touching/dragging
      swiper.on('touchStart', stopAuto);
      swiper.on('sliderMove', stopAuto);
    }
  });
});


// function onWon(prize: { label: string; percent: number; coupon?: string }) {
//     // TODO: Gắn vào promo hệ thống của bạn, ví dụ:
//     // setPromoCode(prize.coupon ?? `SPIN${prize.percent}`)
//     // calcDiscount(subtotal, prize.percent)
//     //console.log('Won:', prize)
// }



</script>

<template>
<div c2c-0 class="page-bg">
    <!-- ================= HERO ================= -->
    <section c2c-1 class="home-hero-2">
        <div c2c-2 class="hero-overlay">
            <div c2c-3 class="hero-container-2">
                <div c2c-4 class="hero-content-2">
                    <h1>Custom Sports Team Banners</h1>
                    <p>
                        Professional 5'x3' Team Banners Starting at <span c2c-5 class="hero-price">$69.99</span><br>
                        + <span c2c-6 class="hero-free-mini">FREE Mini Banner</span> for Every Player
                    </p>
                    <div c2c-7 class="hero-actions">
                        <a href="/baseball-banners" c2c-8 class="btn-primary-lg">
                            Baseball Banners
                        </a>
                        <a href="/softball-banners" c2c-9 class="btn-light">
                            Softball Banners
                        </a>
                    </div>
                </div>
                <!-- Rotating Banner Preview -->
                <div c2c-10 class="hero-rotator-grid">
                    <template v-if="heroImages.length">
                        <div
                          v-for="(img, i) in heroImages"
                          :key="img.__heroSlotKey || img._id || i"
                          c2c-11 class="hero-grid-item"
                        >
                            <a :href="bannerPath(img.label, img._id)">
                                <img
                                  :src="img.img_url || img.jpg_url"
                                  :alt="img.alt || img.label"
                                  loading="eager"
                                  decoding="sync"
                                />
                            </a>
                        </div>
                    </template>
                    <template v-else>
                        <div
                          v-for="slot in heroPlaceholderSlots"
                          :key="`hero-placeholder-${slot}`"
                          c2c-12 class="hero-grid-item hero-grid-item-skeleton"
                          aria-hidden="true"
                        />
                    </template>
                </div>
            </div>
        </div>
    </section>

    <div c2c-13 class="trust-strip">
        <div c2c-14 class="trust-container">
            <div c2c-15 class="trust-item"><span c2c-16 class="trust-icon">✔</span><span c2c-17 class="trust-text">Premium Weather‑Resistant Vinyl</span></div>
            <div c2c-18 class="trust-item"><span c2c-19 class="trust-icon">✔</span><span c2c-20 class="trust-text">High‑Resolution Digital Printing</span></div>
            <div c2c-21 class="trust-item"><span c2c-22 class="trust-icon">✔</span><span c2c-23 class="trust-text">Fast Nationwide Shipping</span></div>
            <div c2c-24 class="trust-item"><span c2c-25 class="trust-icon">✔</span><span c2c-26 class="trust-text">Easy Online Design Tool</span></div>
        </div>
    </div>

    <section c2c-27 class="home-section" v-if="banners">
        <div c2c-28 class="container">
            <h2 class="section-title">Explore Team Banner Designs</h2>
        <ul c2c-29 class="category-list">
            <li v-for="category in banners" :key="category._id" c2c-30 class="category-block">
                <div c2c-31 class="category-head fs-25 tb tc p-12-12-0-12">
                    <div>
                        <p c2c-32 class="category-title upp rm rc">
                            <img c2c-33 class="category-icon m-r-6" v-if="category.alias === 'Soccer Banners'" height="30" style="height: 30px" alt="icon soccer ball" src="https://lct-store.s3.ap-southeast-1.amazonaws.com/localhost/img/0a50bdda-246c-4049-90cd-75a4756f19c5-soccersv.webp">
                            <a c2c-34 class="ff1 fs-25 u" :href="category.url">{{category.alias}}</a>
                        </p>
<!--                        <span>Only </span>-->
<!--                        <span c2c-35 class="c-red fs-35">$59.99</span>-->
<!--                        <span c2c-36 class="lt fs-25 m-l-12">$89.99</span>-->
<!--                        <span> with code</span>-->
                    </div>
<!--                    <p c2c-37 class="fs-13 c-red">+Free mini banners</p>-->
                </div>
                <div c2c-38 class="category-carousel p-12-0">
                    <div c2c-39 class="swiper" :class="category.alias">
                        <ul c2c-40 class="swiper-wrapper >li w-300
                            ^ <2 >li w-46vw">
                            <li c2c-41 class="swiper-slide" v-for="design in category.designs" :key="design._id">
                                <a c2c-42 class="inline-block" :href="bannerPath(design.label, design._id)">
                                    <img c2c-43 class="w-100% br-6" :src="design.img_url || design.jpg_url" :alt="design.alt"/>
                                </a>
                            </li>
                        </ul>
                        <!-- If we need navigation buttons -->
                        <div c2c-44 class="swiper-button-prev"></div>
                        <div c2c-45 class="swiper-button-next"></div>

                        <!-- If we need scrollbar -->
                        <div c2c-46 class="swiper-scrollbar"></div>
                    </div>
                </div>


            </li>
        </ul>
        </div>
    </section>

    <section c2c-47 class="welcome-section">
        <div c2c-48 class="welcome-grid">
            <div c2c-49 class="welcome-media">
                <div c2c-50 class="welcome-image-card">
                    <a href="/design-your-own">
                        <img c2c-51 class="welcome-main-image" src="/banners/desgn-your-own.webp" alt="team banner sports design your own">
                    </a>

                </div>
                <div c2c-52 class="welcome-cta-title">
                    <a c2c-53 class="c-logo-l:-15" href="/design-your-own">
                        🎨 Design & Order Your Custom Team Banner Today!
                    </a>

                </div>
            </div>

            <div c2c-54 class="welcome-copy">
                <h1 class="welcome-title">WELCOME TO TEAM BANNER SPORTS</h1>
                <p>At Team Banner Sports, we specialize in high-quality, full-color sports banners designed to bring pride and energy to your team. Whether it’s soccer, baseball, softball, basketball, or any sport, our durable and vibrant banners will make your team stand out on game day!</p>
                <p c2c-55 class="welcome-subtitle fw-9">
                    WHY CHOOSE TEAM BANNER SPORTS?
                </p>
                <div c2c-56 class="welcome-features m_end ^ >>span c-green">
                    <p>
                        <span c2c-57 class="icon-ok-squared"></span>
                        Premium Quality & Weather-Resistant Materials – Built to last for indoor and outdoor use.
                    </p>
                    <p>
                        <span c2c-58 class="icon-ok-squared"></span>
                        Bright, High-Resolution Digital Printing – Crisp, vivid colors that make an impact.
                    </p>
                    <p>
                        <span c2c-59 class="icon-ok-squared"></span>
                        Easy Online Customization – Use our intuitive banner builder tool to design your own banner!
                    </p>
                    <p>
                        <span c2c-60 class="icon-ok-squared"></span>
                        Fast & Reliable Nationwide Shipping – Get your banner delivered quickly, just in time for the big game!
                    </p>
                </div>
                <p c2c-61 class="welcome-subtitle fw-9"> 🚀 Shipping Times:</p>
                <div c2c-62 class="welcome-shipping">
                    <p>📦 California: 1-3 business days</p>
                    <p>📦 Other States: 3-6 business days</p>
                </div>
            </div>
        </div>
    </section>



    <section c2c-63 class="home-section alt-bg">
        <div c2c-64 class="container">
            <h2 class="section-title">Shop by Sport</h2>
            <div c2c-65 class="sport-grid">
                <div c2c-66 class="sport-card">
                    <a c2c-67 class="block" href="/softball-banners">
                        <img c2c-68 class="" src="/banners/softball-banners.webp" alt="softball team banners">
                    </a>
                    <p>
                        <a c2c-69 class="title" href="/softball-banners">SOFTBALL BANNERS</a>
                    </p>
                    <p c2c-70 class="price">
                        <span>$69.99</span>
                        <span c2c-71 class="lt">$89.99</span>
                    </p>
                    <p>
                        UV Printed 13 oz. Matte Vinyl Banner
                    </p>
                    <p>
                        <span>Size: 5ft x 3ft (60" x 36")</span>
                    </p>
                    <div c2c-72 class="m-t-6 m_end">
                        <p c2c-73 class="fs-17 i">Choose from available templates:</p>
                        <ul c2c-74 class="u tb m_end">
                            <li>
                                <a href="/softball-banners/hem-grommets-softball-banners">Hem & Grommets</a>
                            </li>
                            <li>
                                <a href="/softball-banners/pole-pocket-softball-banners">Pole Pocket</a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div c2c-75 class="sport-card">
                    <a c2c-76 class="block" href="/baseball-banners">
                        <img c2c-77 class="" src="/banners/baseball-banners.webp" alt="baseball team banners">
                    </a>
                    <p>
                        <a c2c-78 class="title" href="/baseball-banners">BASEBALL BANNERS</a>
                    </p>
                    <p c2c-79 class="price">
                        <span>$69.99</span>
                        <span c2c-80 class="lt">$89.99</span>
                    </p>
                    <p>
                        UV Printed 13 oz. Matte Vinyl Banner
                    </p>
                    <p>
                        <span>Size: 5ft x 3ft (60" x 36")</span>
                    </p>
                    <div c2c-81 class="m-t-6 m_end">
                        <p c2c-82 class="fs-17 i">Choose from available templates:</p>
                        <ul c2c-83 class="u tb m_end">
                            <li>
                                <a href="/baseball-banners/hem-grommets-baseball-banners">Hem & Grommets</a>
                            </li>
                            <li>
                                <a href="/baseball-banners/pole-pocket-baseball-banners">Pole Pocket</a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div c2c-84 class="sport-card">
                    <a c2c-85 class="block" href="/soccer-banners">
                        <img c2c-86 class="" src="/banners/soccer-banners.webp" alt="soccer banners">
                    </a>
                    <p>
                        <a c2c-87 class="title block" href="/soccer-banners">
                            SOCCER BANNERS
                        </a>
                    </p>
                    <p c2c-88 class="price">
                        <span>$69.99</span>
                        <span c2c-89 class="lt">$89.99</span>
                    </p>
                    <p>
                        UV Printed 13 oz. Matte Vinyl Banner
                    </p>
                    <p>
                        <span>Size: 5ft x 3ft (60" x 36")</span>
                    </p>
                    <div c2c-90 class="m-t-6 m_end">
                        <p c2c-91 class="fs-17 i">Choose from available templates:</p>
                        <ul c2c-92 class="u tb m_end">
                            <li>
                                <a href="/soccer-banners/hem-grommets-soccer-banners">
                                    Hem & Grommets
                                </a>
                            </li>
                            <li>
                                <a href="/soccer-banners/pole-pocket-soccer-banners">Pole Pocket</a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <div c2c-93 class="design-tool-section tc cc p-12">
        <p c2c-94 class="design-tool-title tb m-t-12 fs-25">Easy Online Tool for Creating Sports Team Banners</p>
        <img c2c-95 class="design-tool-media w-100%-max:960" src="/home/design-tool-team-banners.gif" alt="Design and personalize your softball, baseball, or soccer team banner with our easy-to-use online tool">
    </div>


<!--    <div c2c-96 class="p-12">-->
<!--        When purchasing pennants, please note that we only print rectangular banners and do not offer cutting services for individual shapes like triangles. Your order will be printed as a single large banner containing multiple pennants, which you can cut yourself if needed. This allows us to offer the best possible pricing.-->
<!--    </div>-->



</div>

<!--    <SpinWheelPopup ref="spinPopup"-->
<!--        :show-on-load="true"-->
<!--        :hide-after-win="false"-->
<!--        :lock-after-win="false"-->
<!--        storage-key="tbs_spin_popup_v1"-->

<!--    >-->
<!--        <template #disclaimer>-->
<!--            One spin per visitor. Coupons valid for 24h. Terms apply.-->
<!--        </template>-->
<!--    </SpinWheelPopup>-->

</template>

<style >
/* Thay đổi màu của icon mũi tên */
.swiper-button-prev::after,
.swiper-button-next::after {
    color: white; /* Màu icon */
    font-size: 24px; /* Kích thước icon */
}

/* Thay đổi màu nền của nút */
.swiper-button-prev,
.swiper-button-next {
    background-color: rgba(0, 0, 0, 0.5); /* Màu nền có độ trong suốt */
    width: 40px;
    height: 40px;
    border-radius: 50%; /* Làm tròn nút */
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    pointer-events: auto;
}

/* Hover effect */
.swiper-button-prev:hover,
.swiper-button-next:hover {
    background-color: #007bff; /* Màu nền khi hover */
}

/* Tuỳ chỉnh scrollbar */
.swiper-scrollbar {
    background: rgba(0, 0, 0, 0.1); /* Màu nền scrollbar */
    height: 5px; /* Độ dày của thanh scrollbar */
}

.swiper-scrollbar-drag {
    background: #ff0007; /* Màu thanh trượt */
    border-radius: 5px;
}

/* Make autoplay movement perfectly linear for smooth marquee effect */
.swiper .swiper-wrapper {
    transition-timing-function: linear !important;
}

.home-section {
    padding: 70px 20px;
}

.alt-bg {
    background: #f6f8fb;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
}

.section-title {
    text-align: center;
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 50px;
    color: #1c2b4a;
}

.sport-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;
}

.sport-grid > div {
    background: white;
    padding: 25px;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.08);
    transition: 0.3s ease;
}

.sport-grid > div:hover {
    transform: translateY(-6px);
}

/* ================= PREMIUM HERO LEVEL UP ================= */

.home-hero-2 {
    background: url('/home/slider-home.jpeg') center/cover no-repeat;
    position: relative;
    min-height: 100vh;
    display: flex;
    align-items: center;
    padding: 80px 0;
}

.hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        180deg,
        rgba(10,20,45,0.85) 0%,
        rgba(10,20,45,0.75) 40%,
        rgba(10,20,45,0.85) 100%
    );
}

.hero-content-2 {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    color: white;
}

.hero-content-2 h1 {
    font-size: 46px;
    font-weight: 800;
    margin-bottom: 20px;
}

.hero-content-2 p {
    font-size: 20px;
    margin-bottom: 30px;
}

.hero-actions {
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
}

.btn-primary-lg {
    background: #e53935;
    color: white;
    padding: 14px 30px;
    border-radius: 8px;
    font-weight: 700;
    text-decoration: none;
    font-size: 16px;
}

.btn-primary-lg:hover {
    background: #c62828;
}

.btn-light {
    background: white;
    color: #0f1f3d;
    padding: 12px 28px;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
}

.btn-light:hover {
    background: #f3f3f3;
}

/* ================= TRUST STRIP ================= */

.trust-strip {
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
    padding: 16px 14px;
    border: 1px solid #d6e2f3;
    border-radius: 20px;
    box-shadow: 0 16px 34px rgba(10, 27, 56, 0.11);
    max-width: 1320px;
    margin: 20px auto 24px auto;
}

.trust-container {
    max-width: 1260px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.trust-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 64px;
    padding: 12px 14px;
    border: 1px solid #dce6f4;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
}

.trust-icon {
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    border: 1px solid #b5e3c8;
    background: #eaf9f0;
    color: #0f8a49;
    font-weight: 900;
    font-size: 13px;
    flex-shrink: 0;
}

.trust-text {
    color: #223456;
    font-size: clamp(18px, 1.4vw, 25px);
    line-height: 1.25;
    font-weight: 800;
    text-align: center;
    letter-spacing: -0.2px;
}

.category-list {
    display: grid;
    gap: 24px;
}

.category-block {
    margin-bottom: 0 !important;
    border: 1px solid #e8edf5;
    border-radius: 18px;
    background: #fdfefe;
    box-shadow: 0 12px 30px rgba(14, 33, 68, 0.06);
    overflow: hidden;
}

.category-head {
    padding: 18px 18px 10px 18px !important;
    border-bottom: 1px solid #edf2f8;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.category-title {
    margin: 0 !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    line-height: 1.2;
}

.category-icon {
    width: 30px;
    height: 30px;
    display: inline-block;
    object-fit: contain;
    vertical-align: middle;
    margin-top: -1px;
}

.category-carousel {
    padding: 14px 14px 18px 14px !important;
}

/* ================= WELCOME SECTION UPGRADE ================= */

.welcome-section {
    background: #ffffff;
    padding: 72px 20px;
    border-radius: 22px;
    margin: 24px auto 60px auto;
    border: 1px solid #d9e3f1;
    box-shadow: 0 18px 44px rgba(10, 27, 56, 0.12);
    max-width: 1320px;
}

.welcome-grid {
    display: grid;
    grid-template-columns: minmax(300px, 1fr) minmax(360px, 1fr);
    gap: 34px;
    align-items: start;
}

.welcome-image-card {
    overflow: hidden;
    border-radius: 16px;
    border: 1px solid #e6ecf6;
    background: #ffffff;
    box-shadow: 0 14px 30px rgba(17, 38, 74, 0.08);
}

.welcome-main-image {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
}

.welcome-cta-title a {
    display: inline-block;
    color: #0d2a58 !important;
    text-decoration: none;
    font-size: clamp(22px, 2.2vw, 34px);
    font-weight: 800;
    margin-top: 14px;
}

.welcome-copy {
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid #edf2f8;
    padding: 22px 24px;
    text-align: center;
}

.welcome-title {
    margin-top: 0 !important;
    margin-bottom: 14px !important;
    color: #10294f;
    font-size: clamp(24px, 2.2vw, 34px);
}

.welcome-subtitle {
    margin-top: 16px !important;
    margin-bottom: 8px !important;
    color: #0d2a58;
}

.welcome-features p {
    margin-bottom: 8px;
    text-align: left;
}

.welcome-shipping {
    background: #f5f9ff;
    border: 1px dashed #bfd2eb;
    border-radius: 12px;
    padding: 10px 12px;
    text-align: left;
}

.welcome-shipping p {
    margin: 4px 0;
}

@media (max-width: 768px) {
    .home-hero-2 {
        height: 420px;
    }

    .hero-content-2 h1 {
        font-size: 32px;
    }
}
/* ===== HERO TWO COLUMN LAYOUT ===== */

.hero-container-2 {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 40px;
    position: relative;
    z-index: 2;
}

.hero-content-2 {
    text-align: left;
}


/* Responsive */
@media (max-width: 900px) {
    .hero-container-2 {
        grid-template-columns: 1fr;
        text-align: center;
    }

    .hero-content-2 {
        text-align: center;
    }

    .home-hero-2 {
        height: auto;
        padding: 60px 0;
    }

    .hero-rotator-grid {
        grid-template-columns: 1fr;
        max-width: 100%;
        margin-top: 30px;
    }

    .hero-grid-item img {
        box-shadow: 0 15px 35px rgba(0,0,0,0.35);
    }
}

/* ===== HERO 2x2 GRID ===== */
.hero-rotator-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
    width: 100%;
    max-width: 520px;
    margin: 40px auto 0 auto;
}

/* HERO GRID ITEM FIXED RATIO */
.hero-grid-item {
    aspect-ratio: 5 / 3;
    border-radius: 18px;
    overflow: hidden;
    background: rgba(255,255,255,0.05);
    backdrop-filter: blur(6px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.35);
    transition: transform 0.4s ease;
}

.hero-grid-item img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
}

.hero-grid-item-skeleton {
    background: linear-gradient(110deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.16) 35%, rgba(255,255,255,0.08) 45%);
    background-size: 220% 100%;
    animation: heroSkeletonPulse 1.4s ease-in-out infinite;
}

@keyframes heroSkeletonPulse {
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: -100% 0;
    }
}

/* ================= VISUAL POLISH IMPROVEMENTS ================= */

.page-bg {
    padding-bottom: 28px;
    background: linear-gradient(
        180deg,
        #e9eff8 0%,
        #e3ebf6 calc(100% - 28px),
        #0f1f3d calc(100% - 28px),
        #0f1f3d 100%
    );
}

/* Make sections feel like blocks instead of one long page */
.home-section {
    padding: 80px 20px;
    background: #ffffff;
    margin: 24px auto 40px auto;
    border-radius: 20px;
    border: 1px solid #d9e3f1;
    box-shadow: 0 18px 44px rgba(10, 27, 56, 0.12);
    max-width: 1320px;
}

.home-section.alt-bg {
    background: #ffffff;
}

/* Improve template section spacing */
.home-section ul > li {
    margin-bottom: 60px;
}

/* Improve category titles */
.home-section .fs-25 {
    font-size: 22px;
    margin-bottom: 20px;
    color: #1c2b4a;
}

/* Improve swiper card look */
.swiper-slide img {
    border-radius: 10px;
    transition: 0.3s ease;
}

.swiper-slide img:hover {
    transform: scale(1.04);
}

/* Improve design tool section */
.tc.cc.p-12 {
    background: white;
    padding: 80px 20px;
    border-radius: 20px;
    margin: 24px auto 0 auto;
    max-width: 1320px;
}

.design-tool-section {
    border: 1px solid #d9e3f1;
    box-shadow: 0 18px 44px rgba(10, 27, 56, 0.12);
}

.design-tool-title {
    color: #10294f;
    margin-bottom: 16px;
}

.design-tool-media {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
    border-radius: 14px;
    border: 1px solid #e8edf5;
}

/* Improve Shop by Sport cards */
.sport-grid > div {
    background: white;
    padding: 30px;
    border-radius: 16px;
    box-shadow: 0 15px 40px rgba(0,0,0,0.08);
    transition: 0.3s ease;
}

.sport-grid > div:hover {
    transform: translateY(-8px);
}

.sport-card {
    border: 1px solid #e8edf5;
}

.sport-card > a img {
    width: 100%;
    border-radius: 12px;
}

.sport-card .title {
    color: #0d2a58;
    text-decoration: none;
    font-weight: 800;
}

.sport-card .price > span:first-child {
    color: #dc2e2e;
    font-weight: 800;
}

/* Improve overall typography rhythm */
p {
    line-height: 1.7;
}

/* Remove overly tight layout feeling on large screens */
.container {
    max-width: 1280px;
}

.hero-grid-item {
    transition: transform 0.6s ease, opacity 0.6s ease;
}
/* ================= HERO PREMIUM OVERRIDE ================= */

.home-hero-2 {
    min-height: clamp(360px, 48vh, 560px) !important;
    padding: 24px 0 !important;
}

.hero-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    background: linear-gradient(
        135deg,
        rgba(8,18,40,0.95) 0%,
        rgba(8,18,40,0.85) 40%,
        rgba(8,18,40,0.75) 100%
    ) !important;
}

.hero-container-2 {
    width: 100%;
    max-width: 1280px !important;
    padding: 0 40px !important;
    grid-template-columns: 1.1fr 1fr !important;
    gap: 56px !important;
}

.hero-content-2 {
    max-width: 580px !important;
}

.hero-content-2 h1 {
    font-size: 52px !important;
    font-weight: 900 !important;
    line-height: 1.1 !important;
    letter-spacing: -1px !important;
    margin-top: 0 !important;
    margin-bottom: 20px !important;
}

.hero-content-2 p {
    font-size: 20px !important;
    line-height: 1.6 !important;
    margin-top: 0 !important;
    margin-bottom: 36px !important;
    opacity: 0.95;
}

.hero-price {
    display: inline-block;
    font-size: 1.32em;
    font-weight: 900;
    line-height: 1;
    color: #ffd54f;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
}

.hero-free-mini {
    display: inline-block;
    font-weight: 900;
    color: #7df9a7;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.hero-rotator-grid {
    gap: 22px !important;
    max-width: 560px !important;
    margin: 0 auto !important;
    align-self: center !important;
}

.hero-grid-item {
    border-radius: 20px !important;
    backdrop-filter: blur(10px) !important;
    box-shadow: 0 30px 70px rgba(0,0,0,0.45) !important;
    transition: transform 0.5s ease, box-shadow 0.5s ease !important;
}

.hero-grid-item:hover {
    transform: translateY(-8px) scale(1.02) !important;
    box-shadow: 0 40px 90px rgba(0,0,0,0.55) !important;
}

@media (max-width: 1200px) {
    .home-hero-2 {
        display: block !important;
        height: auto !important;
        min-height: 0 !important;
        padding: 0 !important;
    }

    .hero-overlay {
        position: relative !important;
        inset: auto !important;
        display: block !important;
        width: 100% !important;
        padding: 28px 0 12px 0 !important;
    }

    .hero-container-2 {
        grid-template-columns: 1fr !important;
        text-align: center !important;
        gap: 24px !important;
        padding: 0 20px !important;
    }

    .hero-content-2 {
        max-width: 860px !important;
        margin: 0 auto !important;
    }

    .hero-content-2 h1 {
        font-size: clamp(34px, 5.4vw, 52px) !important;
    }

    .hero-actions {
        justify-content: center !important;
        gap: 12px !important;
    }

    .hero-rotator-grid {
        max-width: min(94vw, 560px) !important;
        gap: 12px !important;
        margin: 8px auto 0 auto !important;
    }

    .welcome-grid {
        grid-template-columns: 1fr;
    }

    .trust-container {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 900px) {
    .home-hero-2 {
        padding: 0 !important;
    }

    .hero-overlay {
        padding: 20px 0 8px 0 !important;
    }

    .hero-container-2 {
        gap: 16px !important;
        padding: 0 14px !important;
    }

    .hero-content-2 h1 {
        font-size: clamp(28px, 8.6vw, 38px) !important;
    }

    .hero-content-2 p {
        font-size: 17px !important;
        line-height: 1.45 !important;
        margin-bottom: 20px !important;
    }

    .hero-price {
        font-size: 1.22em;
    }

    .hero-free-mini {
        font-size: 1.02em;
    }

    .hero-actions {
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 10px !important;
    }

    .hero-actions a {
        width: min(92vw, 360px) !important;
        padding: 12px 16px !important;
        font-size: 16px !important;
    }

    .hero-rotator-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        max-width: min(92vw, 420px) !important;
        gap: 10px !important;
        margin: 8px auto 0 auto !important;
    }
}

@media (max-width: 560px) {
    .hero-content-2 h1 {
        font-size: clamp(30px, 11vw, 40px) !important;
        letter-spacing: -0.5px !important;
    }

    .hero-content-2 p {
        font-size: 16px !important;
    }

    .hero-price {
        font-size: 1.16em;
    }

    .hero-free-mini {
        font-size: 1em;
    }

    .hero-actions a {
        font-size: 20px !important;
    }

    .hero-rotator-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        max-width: min(94vw, 380px) !important;
        gap: 8px !important;
    }

    .trust-container {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .trust-item {
        min-height: 56px;
        padding: 10px 12px;
    }

    .trust-text {
        font-size: 18px;
    }

    .category-head {
        padding: 14px 12px 8px 12px !important;
    }

    .category-carousel {
        padding: 10px 10px 14px 10px !important;
    }

    .welcome-section {
        padding: 42px 14px;
    }

    .welcome-copy {
        padding: 16px 14px;
    }

    .trust-strip,
    .home-section,
    .welcome-section,
    .design-tool-section {
        margin-left: 10px !important;
        margin-right: 10px !important;
        border-radius: 14px !important;
    }
}
</style>
<style lang="scss" scoped>.fs-25[c2c-31] {
                        font-size: 25px;
                 }.tb[c2c-31] {
                        font-weight: bold;
                 }.tc[c2c-31] {
                        text-align: center;
                 }.p-12-12-0-12[c2c-31] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 0;padding-left: 12px;
                 }.upp[c2c-32] {
                        text-transform: uppercase;
                 }.rm[c2c-32] {
                        display: flex; flex-direction: row; align-items: center;
                 }.rc[c2c-32] {
                        display: flex; flex-direction: row; justify-content: center;
                 }.m-r-6[c2c-33] {
                        margin-right: 6px;
                 }.fs-25[c2c-34] {
                        font-size: 25px;
                 }.u[c2c-34] {
                        text-decoration: underline;
                 }.c-red[c2c-35] {
                        color: hsl(0, 100%, 50%);
                 }.fs-35[c2c-35] {
                        font-size: 35px;
                 }.lt[c2c-36] {
                        text-decoration: line-through;
                 }.fs-25[c2c-36] {
                        font-size: 25px;
                 }.m-l-12[c2c-36] {
                        margin-left: 12px;
                 }.fs-13[c2c-37] {
                        font-size: 13px;
                 }.c-red[c2c-37] {
                        color: hsl(0, 100%, 50%);
                 }.p-12-0[c2c-38] {
                        padding-top: 12px;padding-right: 0;padding-bottom: 12px;padding-left: 0;
                 }.\>li.w-300[c2c-40] > li {
                        width: 300px;
                 }@media (max-width: 768px) {.\<2.\>li.w-46vw[c2c-40] > li {
                        width: 46vw;
                 }}.inline-block[c2c-42] {
                        display: inline;
                 }.w-100\%[c2c-43] {
                        width: 100%;
                 }.br-6[c2c-43] {
                        border-top-left-radius: 6px;border-top-right-radius: 6px;border-bottom-right-radius: 6px;border-bottom-left-radius: 6px;
                 }.c-logo-l\:-15[c2c-53] {
                        color: hsl(197.39999999999998, 95.9%, 23%);
                 }.fw-9[c2c-55] {
                        font-weight: 900;
                 }.\>\>span.c-green[c2c-56] span {
                        color: hsl(120, 100%, 25.1%);
                 }.fw-9[c2c-61] {
                        font-weight: 900;
                 }.block[c2c-67] {
                        display: block;
                 }.lt[c2c-71] {
                        text-decoration: line-through;
                 }.m-t-6[c2c-72] {
                        margin-top: 6px;
                 }.fs-17[c2c-73] {
                        font-size: 17px;
                 }.i[c2c-73] {
                        font-style: italic;
                 }.u[c2c-74] {
                        text-decoration: underline;
                 }.tb[c2c-74] {
                        font-weight: bold;
                 }.block[c2c-76] {
                        display: block;
                 }.lt[c2c-80] {
                        text-decoration: line-through;
                 }.m-t-6[c2c-81] {
                        margin-top: 6px;
                 }.fs-17[c2c-82] {
                        font-size: 17px;
                 }.i[c2c-82] {
                        font-style: italic;
                 }.u[c2c-83] {
                        text-decoration: underline;
                 }.tb[c2c-83] {
                        font-weight: bold;
                 }.block[c2c-85] {
                        display: block;
                 }.block[c2c-87] {
                        display: block;
                 }.lt[c2c-89] {
                        text-decoration: line-through;
                 }.m-t-6[c2c-90] {
                        margin-top: 6px;
                 }.fs-17[c2c-91] {
                        font-size: 17px;
                 }.i[c2c-91] {
                        font-style: italic;
                 }.u[c2c-92] {
                        text-decoration: underline;
                 }.tb[c2c-92] {
                        font-weight: bold;
                 }.tc[c2c-93] {
                        text-align: center;
                 }.cc[c2c-93] {
                        display: flex; flex-direction: column; align-items: center;
                 }.p-12[c2c-93] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.tb[c2c-94] {
                        font-weight: bold;
                 }.m-t-12[c2c-94] {
                        margin-top: 12px;
                 }.fs-25[c2c-94] {
                        font-size: 25px;
                 }.w-100\%-max\:960[c2c-95] {
                        width: 100%;max-width: 960px;
                 }.p-12[c2c-96] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }</style>